/**
 * Legend of the (single) volume pane: CME volume, and on MNQ/NQ the Vest
 * NQ-PERP bar of the hovered (else latest) candle - Vest units and
 * chart-contract equivalent, buy/sell/delta, share of notional - plus each
 * venue's relative volume when it was evaluated.
 *
 * The eye button in the pane's top-right corner hides / shows that text
 * (persisted per device, volumePaneSettingsStore.legendVisible). Hidden means
 * hidden: no hover readout either, only the button stays.
 */
import React, { useMemo } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useVolumePaneSettingsStore } from '../../store/volumePaneSettingsStore';
import { useVestVolumeStore } from '../../store/vestVolumeStore';
import { useVestSpikeSettingsStore } from '../../store/vestSpikeSettingsStore';
import { aggregateVestBars, pointForCandle, volumeLegend, ChartCandle } from '../../utils/vestVolume';
import { computeVestRelVols } from '../../utils/vestSpikes';
import { RelVol, relVolBasisText, relVolLabel } from '../../utils/cmeVolumeSpikes';

export interface VolumePaneLegendProps {
  symbol: string;
  candles: readonly ChartCandle[];
  hoveredTime: number | null;
  timeframeMinutes: number;
  vestOn: boolean;
  cmeRel: ReadonlyMap<number, RelVol> | null;
  /** Show the Vest rel vol (volume-pane Vest spikes toggle). */
  vestRelOn: boolean;
}

export function legendLines(
  props: VolumePaneLegendProps,
  points: ReturnType<typeof aggregateVestBars>,
  vestRel: ReturnType<typeof computeVestRelVols> | null,
): string[] {
  const { symbol, candles, hoveredTime, timeframeMinutes, vestOn, cmeRel } = props;
  const candle = hoveredTime !== null ? candles.find((c) => c.time === hoveredTime) : candles[candles.length - 1];
  const time = candle?.time ?? null;
  const point = vestOn && time !== null ? pointForCandle(points, time, timeframeMinutes) : undefined;
  const lines = volumeLegend({
    symbol,
    cmeVolume: candle ? candle.volume ?? 0 : null,
    chartPrice: candle?.close ?? null,
    point,
    vestOn,
  });
  const cr = time !== null ? cmeRel?.get(time) : undefined;
  if (cr) lines[0] += ` · ${relVolLabel(cr.relVol)} ${relVolBasisText(cr)}`;
  const vr = vestOn && time !== null ? vestRel?.get(time) : undefined;
  if (vr && lines.length > 1) lines[1] += ` · ${relVolLabel(vr.relVol)} ${relVolBasisText(vr)}`;
  return lines;
}

export const VolumePaneTextToggle: React.FC = () => {
  const visible = useVolumePaneSettingsStore((s) => s.legendVisible);
  const setVisible = useVolumePaneSettingsStore((s) => s.setLegendVisible);
  const label = visible ? 'Hide volume pane text' : 'Show volume pane text';
  return (
    <button
      type="button"
      className={`volume-pane-text-toggle ${visible ? '' : 'is-hidden'}`}
      data-testid="volume-pane-text-toggle"
      aria-label={label}
      aria-pressed={!visible}
      title={label}
      // Keep the tap off the chart below (pan, long-press alert, crosshair).
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        setVisible(!visible);
      }}
    >
      {visible ? <Eye size={13} /> : <EyeOff size={13} />}
    </button>
  );
};

const LegendText: React.FC<VolumePaneLegendProps> = (props) => {
  const bars = useVestVolumeStore((s) => s.bars);
  const lookback = useVestSpikeSettingsStore((s) => s.lookback);
  const threshold = useVestSpikeSettingsStore((s) => s.threshold);
  const baseline = useVestSpikeSettingsStore((s) => s.baseline);
  const { vestOn, vestRelOn, timeframeMinutes } = props;
  const points = useMemo(() => (vestOn ? aggregateVestBars(bars.values(), timeframeMinutes) : new Map()), [bars, timeframeMinutes, vestOn]);
  const vestRel = useMemo(
    () => (vestOn && vestRelOn ? computeVestRelVols(bars.values(), timeframeMinutes, { lookback, threshold, baseline }, Date.now()) : null),
    [bars, timeframeMinutes, vestOn, vestRelOn, lookback, threshold, baseline],
  );
  const lines = legendLines(props, points, vestRel);
  return (
    <div className="volume-pane-label volume-pane-legend" data-testid="volume-pane-legend">
      {lines.map((l, i) => (
        <div key={i} className={i === 0 ? 'volume-legend-cme' : 'volume-legend-vest'}>
          {l}
        </div>
      ))}
    </div>
  );
};

/** Legend text (unless hidden) + the hide/show button. */
const VolumePaneLegend: React.FC<VolumePaneLegendProps> = (props) => {
  const visible = useVolumePaneSettingsStore((s) => s.legendVisible);
  return (
    <>
      {visible && <LegendText {...props} />}
      <VolumePaneTextToggle />
    </>
  );
};

export default VolumePaneLegend;
